import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import { useProducts } from '@/common/hooks/queries/useProducts'
import useDebounce from '@/common/hooks/useDebounce'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { Mapper } from '@/common/services/mapper'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useCreateProductNfceMapping } from '@/features/productNfceMappings/hooks/useCreateProductNfceMapping'
import { createSchema, ProductNfceMappingValidationSchema } from '@/features/productNfceMappings/schemas/create.schema'
import { ProductData } from '@/features/products/types/product'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRight } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'

const Create = () => {
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)
  const [searchProduct, setSearchProduct] = useDebounce('')

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm<ProductNfceMappingValidationSchema>({
    resolver: zodResolver(createSchema),
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasCreatePermission = !isUserPermissionsPending ? hasUserPermission('Admin::ProductNfceMappings::Create') : true

  const { data: productOptions } = useProducts<{ label: string; value: string }[]>({
    filters: { search: searchProduct },
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { mutate: createProductNfceMapping, isPending: createIsPending } = useCreateProductNfceMapping({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productNfceMappings'] })
      goBack({ fallback: '/product-nfce-mappings' })
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<ProductNfceMappingValidationSchema> = (data) => createProductNfceMapping(data)

  return (
    <ErrorMessageWrapper className="h-[calc(100vh-128px)]" statusCode={hasCreatePermission ? 200 : 403}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="false">
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/product-nfce-mappings" />

            <p className="text-white-400 font-normal">Mapeamento de Produtos NFCe</p>

            <ChevronsRight className="text-white-400" />

            <h2>Novo mapeamento</h2>
          </div>

          <div className="flex gap-4">
            <Link to="/product-nfce-mappings">
              <Button variant="outline-error">Cancelar</Button>
            </Link>

            <Button
              data-testid="create_product_nfce_mapping_button"
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={createIsPending}
            >
              Confirmar
            </Button>
          </div>
        </div>

        <Box className="flex max-h-[calc(100vh-128px)] flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto">
          <p className="text-lg font-bold">Novo mapeamento</p>

          <div className="flex gap-2">
            <TextField
              label="Nome"
              markAsRequired
              errorMessage={errors?.name?.message || undefined}
              className="w-1/2"
              {...register('name')}
              disabled={createIsPending}
            />
            <div className="w-1/2">
              <SelectController
                control={control}
                name="product_id"
                label="Produto"
                markAsRequired
                onInputChange={setSearchProduct}
                options={productOptions || []}
                errorMessage={errors?.product_id?.message}
              />
            </div>
          </div>
        </Box>

        <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
      </form>
    </ErrorMessageWrapper>
  )
}

export default Create
